"use client"

import { useState, useMemo } from "react"
import { Post } from "./BookPage" // パスを修正
import SingleBook from "../components/SingleBook"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"

type SortOption =
  | "none"
  | "dateAsc"
  | "dateDesc"
  | "favoriteCountAsc"
  | "favoriteCountDesc"

interface BookSorterProps {
  books: Post[]
}

export default function BookSorter({ books }: BookSorterProps) {
  const [sortOption, setSortOption] = useState<SortOption>("none")
  const [selectedTag, setSelectedTag] = useState("")

  // すべてのタグを重複なく収集し、五十音順にソート
  const allTags = useMemo(() => {
    const tagSet = new Set<string>()
    books.forEach((book) => {
      book.tags.forEach((tag) => tagSet.add(tag))
    })
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b, "ja"))
  }, [books])

  // フィルタ + ソート
  const filteredAndSorted = useMemo(() => {
    // タグフィルタ
    let filtered = books
    if (selectedTag) {
      filtered = books.filter((book) => book.tags.includes(selectedTag))
    }

    // ソート
    const sorted = [...filtered].sort((a, b) => {
      const dateA = a.date ? new Date(a.date) : new Date(0)
      const dateB = b.date ? new Date(b.date) : new Date(0)

      if (sortOption === "dateAsc") {
        return dateA.getTime() - dateB.getTime() // 古い順
      } else if (sortOption === "dateDesc") {
        return dateB.getTime() - dateA.getTime() // 新しい順
      } else if (sortOption === "favoriteCountAsc") {
        return a.favoritesCount - b.favoritesCount // 少ない順
      } else if (sortOption === "favoriteCountDesc") {
        return b.favoritesCount - a.favoritesCount // 多い順
      }
      return 0 // "none"
    })

    return sorted
  }, [books, selectedTag, sortOption])

  // リセットボタンの処理
  const handleReset = () => {
    setSortOption("none")
    setSelectedTag("")
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        {/* 並べ替え */}
        <div className="flex items-center space-x-2">
          <Label>並べ替え</Label>
          <Select
            value={sortOption}
            onValueChange={(val) => setSortOption(val as SortOption)}
          >
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="並べ替えなし" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">並べ替えなし</SelectItem>
              <SelectItem value="favoriteCountDesc">オススメ多い順</SelectItem>
              <SelectItem value="favoriteCountAsc">オススメ少ない順</SelectItem>
              <SelectItem value="dateDesc">新しい順</SelectItem>
              <SelectItem value="dateAsc">古い順</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* タグ選択 */}
        <div className="flex items-center space-x-2">
          <Label>フィルタ</Label>
          <Select
            value={selectedTag ? selectedTag : "__ALL__"}
            onValueChange={(val) => {
              if (val === "__ALL__") {
                setSelectedTag("")
              } else {
                setSelectedTag(val)
              }
            }}
          >
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="フィルタなし" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">フィルタなし</SelectItem>
              {allTags.map((tag) => (
                <SelectItem key={tag} value={tag}>
                  {tag}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="secondary" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      {/* 結果表示 */}
      <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-2">
        {filteredAndSorted.map((book) => (
          <SingleBook
            key={book.id}
            id={book.id}
            title={book.title}
            description={book.description}
            date={book.date || ""}
            tags={book.tags}
            slug={book.slug}
            amazonUrl={book.amazonUrl}
            rakutenUrl={book.rakutenUrl}
            initialPic={"/nowLoading.png"}
            isPaginationPage={false}
          />
        ))}
      </div>
    </div>
  )
}
